/* eslint-disable react/jsx-one-expression-per-line */
import React, { useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  useFormContext, useWatch, useFieldArray,
} from 'react-hook-form';
import CustomBtn from '../../../../../../generic/CustomBtn';
import CustomCheckbox from '../../../../../../generic/CustomCheckbox';
import CustomTextarea from '../../../../../../generic/CustomTextarea';
import CustomInput from '../../../../../../generic/CustomInput';
import { CustomReactSelect as CustomSelect } from '../../../../../../generic/CustomSelect/CustomSelect';
import Tooltip from '../../../../../../components/Tooltip';
import { ReactComponent as TooltipIcon } from '../../../../../../assets/image/carbon/circle-exclamation.svg';
import './ProjectActivity.scss';
import { upNumberString } from '../../../../../../utils/helpers';
import NotificationAlert from '../../../../../../generic/NotificationAlert';

const ArrayFields = ({ name, register, rules, control, errors, disabled, projectDuration }) => {
  const translate = useIntl();
  const durationOptions = new Array(40).fill('').map((el, index) => ({
    label: `${index + 1} ${index === 0 ? translate.formatMessage({ id: 'carbon.placeholder.duration-year' }) : translate.formatMessage({ id: 'carbon.placeholder.duration-years' })}`,
    value: index + 1,
  }));
  durationOptions.unshift({
    label: translate.formatMessage({ id: 'carbon.placeholder.duration-text' }),
    value: 'projectDuration',
  });
  const { fields, append, remove } = useFieldArray({ control, name });
  const { setValue } = useFormContext();

  const checkYearsInOption = (value, nameField) => {
    if (value === 'projectDuration') {
      const rightNumber = durationOptions.find((el) => el.value === (+projectDuration));
      if (rightNumber) {
        setValue(nameField, { label: rightNumber.label,
          value: rightNumber.value });
      } else {
        setValue(nameField, { label: ` ${(+projectDuration)} ${translate.formatMessage({ id: 'carbon.placeholder.duration-years' })}`,
          value: (+projectDuration) });
      }
    }
  };
  const frequencyOptions = useMemo(() => ([
    { value: 'one-shot', label: translate.formatMessage({ id: 'select.option.oneShot' }) },
    { value: 'once-per-year', label: translate.formatMessage({ id: 'select.option.oncePerYear' }) },
    { value: 'twice-per-year', label: translate.formatMessage({ id: 'select.option.twicePerYear' }) },
    { value: 'three-times-per-year', label: translate.formatMessage({ id: 'select.option.threeTimesPerYear' }) },
    { value: 'more', label: translate.formatMessage({ id: 'select.option.more' }) },
  ]), []);
  return (
    <div className="array-fields__wrapper">
      <div className="array-fields">
        {fields.map((field, index) => (
          <div className="array-fields__item" key={field.id}>
            <CustomInput
              type="text"
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.productName' })}
              customClass="input-field"
              name={`${name}[${index}].productName`}
              value={field.productName}
              register={register(rules)}
              error={errors?.[index]?.productName}
              disabled={disabled}
            />
            <CustomInput
              type="text"
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.tonnes' })}
              customClass="input-field"
              name={`${name}[${index}].tonnes`}
              value={field.tonnes}
              useNumberFormat
              numberFormatParams={{ allowNegative: false, thousandSeparator: true }}
              control={control}
              rules={{ ...rules,
                validate: {
                  leadingZeros: (value) => {
                    const warning = translate.formatMessage({ id: 'carbon.form.invalidValue' });
                    if ((+value % 1) !== 0) {
                      return ((+value > 0 && +value[0] === 0) || warning);
                    }
                    return (+value[0] !== 0 || warning);
                  },
                } }}
              error={errors?.[index]?.tonnes}
              disabled={disabled}
            />
            <CustomSelect
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.frequency' })}
              options={frequencyOptions}
              inputClass="select-input"
              name={`${name}[${index}].frequency`}
              defaultValue={field.frequency}
              control={control}
              rules={rules}
              error={errors?.[index]?.frequency}
              disabled={disabled}
            />
            <CustomSelect
              placeholder={translate.formatMessage({ id: 'carbon.placeholder.duration' })}
              options={durationOptions}
              inputClass="select-input"
              name={`${name}[${index}].duration`}
              defaultValue={field.duration}
              control={control}
              rules={rules}
              error={errors?.[index]?.duration}
              disabled={disabled}
              selectOnChange={(e) => checkYearsInOption(e, `${name}[${index}].duration`)}
            />
            {!disabled && index !== 0 && (
              <CustomBtn
                label=""
                iconClass="icon-trash"
                handleClick={() => remove(index)}
                customClass="array-fields__bin-button"
              />
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="array-fields__add-wrapper">
          <CustomBtn
            label={translate.formatMessage({ id: 'button-addProduct' })}
            iconClass="icon-rect-plus"
            handleClick={() => append({ productName: '', tonnes: '', frequency: undefined })}
            customClass="array-fields__add-button"
          />
        </div>
      )}
    </div>
  );
};
const preActivices = ['carbon.form.prepActTooltip.clearing', 'carbon.form.prepActTooltip.clearing', 'carbon.form.prepActTooltip.grading', 'carbon.form.prepActTooltip.soil', 'carbon.form.prepActTooltip.weedControl', 'carbon.form.prepActTooltip.pest', 'carbon.form.prepActTooltip.site'];
const genActivices = ['carbon.form.prepActTooltip.assessment', 'carbon.form.prepActTooltip.planning', 'carbon.form.prepActTooltip.integration', 'carbon.form.prepActTooltip.monitoring'];
const ProjectActivity = ({ defaultValues, tabKey, isActiveTab, disabled, projectData }) => {
  const translate = useIntl();
  const { register, errors, control } = useFormContext();
  const { [tabKey]: formErrors } = errors;
  const [statusAlert, setStatusAlert] = useState({ generalPlanning: false, treeSpeciesNumber: false });
  const changeStatusAlert = (type, value) => {
    if (type === 'treeSpeciesNumber') {
      const isWarning = +value > 100;
      return setStatusAlert({ ...statusAlert, [type]: isWarning });
    }
    const isWarning = value.length < 150;

    setStatusAlert({ ...statusAlert, [type]: isWarning });
  };
  const prepActTooltipBody = (idTitle, arrayIds) => (
    <div className="prep-act-tooltip">
      <FormattedMessage
        id={idTitle}
        values={{
          p: (...chunks) => <p>{chunks}</p>,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
      <ul>
        {arrayIds.map((el) => (
          <FormattedMessage
            id={el}
            values={{
              li: (...chunks) => <li key={el}>{chunks}</li>,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        ))}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.clearing" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.grading" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.soil" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.weedControl" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.pest" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
        {/* <FormattedMessage */}
        {/*  id="carbon.form.prepActTooltip.site" */}
        {/*  values={{ */}
        {/*    li: (...chunks) => <li>{chunks}</li>, */}
        {/*    b: (chunks) => <b>{chunks}</b>, */}
        {/*  }} */}
        {/* /> */}
      </ul>
    </div>
  );

  const getTooltip = (body = '', id = '') => (
    <Tooltip
      wrapperClass="tooltip"
      targetElement={<TooltipIcon data-tip data-iscapture data-for={id} />}
      tooltipElement={body}
      tooltipProps={{ className: 'tooltip__message', id }}
    />
  );

  const isTrueString = (s) => s === 'true';
  const watchArePestWeedControlUsed = useWatch({ name: `${tabKey}.arePestWeedControlUsed`, exact: true });
  const watchAreFertilizersUsed = useWatch({ name: `${tabKey}.areFertilizersUsed`, exact: true });
  const watchAreExoticSpeciesPlanted = useWatch({ name: `${tabKey}.areExoticSpeciesPlanted`, exact: true });
  return (
    <div className="project-activity__wrapper" style={{ display: isActiveTab ? 'block' : 'none' }}>
      <div className="project-activity">
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.generalPlanning' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea={`textarea-field${statusAlert?.generalPlanning ? ' warning' : ''}`}
            customClassLabel="form-field-title"
            name={`${tabKey}.generalPlanning`}
            tooltip={getTooltip(prepActTooltipBody('carbon.form.prepActTooltip.pleaseDetailSec', genActivices), 'site-gen-activities')}
            value={defaultValues.generalPlanning}
            register={register({ required: true })}
            error={formErrors?.generalPlanning}
            disabled={disabled}
            change={(e) => {
              changeStatusAlert('generalPlanning', e.target.value);
            }}
          />
          {statusAlert?.generalPlanning && <NotificationAlert text={translate.formatMessage({ id: 'carbon.form.generalPlanning.warning' })} />}

        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.sitePreparationActivities' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            tooltip={getTooltip(prepActTooltipBody('carbon.form.prepActTooltip.pleaseDetail', preActivices), 'site-preparation-activities')}
            name={`${tabKey}.sitePreparationActivities`}
            value={defaultValues.sitePreparationActivities}
            register={register({ required: true })}
            error={formErrors?.sitePreparationActivities}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.willZoneBeBurned' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.willZoneBeBurned`}
            value={defaultValues.willZoneBeBurned}
            register={register({ required: true })}
            error={formErrors?.willZoneBeBurned}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.arePestWeedControlUsed"
              values={{
                p: (...chunks) => <p className="form-field-title">{chunks}</p>,
              }}
            />
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.arePestWeedControlUsed`}
                defaultChecked={isTrueString(defaultValues.arePestWeedControlUsed)}
                register={register({ required: true })}
                error={formErrors?.arePestWeedControlUsed}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.arePestWeedControlUsed`}
                defaultChecked={isTrueString(defaultValues.arePestWeedControlUsed)}
                register={register({ required: true })}
                error={formErrors?.arePestWeedControlUsed}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.pestWeedProducts"
              values={{
                p: (...chunks) => <p className="form-field-title">{chunks}</p>,
              }}
            />
            <div className="project-activity__array-fields">
              <ArrayFields
                name={`${tabKey}.pestWeedProducts`}
                control={control}
                register={register}
                rules={{ required: isTrueString(watchArePestWeedControlUsed) }}
                errors={formErrors?.pestWeedProducts}
                disabled={disabled}
                projectDuration={projectData?.projectDuration}
              />
            </div>
          </div>
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.seedlingsComeFrom' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.seedlingsComeFrom`}
            value={defaultValues.seedlingsComeFrom}
            register={register({ required: true })}
            error={formErrors?.seedlingsComeFrom}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.typeOfTreePlantingActivities' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.typeOfTreePlantingActivities`}
            value={defaultValues.typeOfTreePlantingActivities}
            register={register({ required: true })}
            error={formErrors?.typeOfTreePlantingActivities}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.areFertilizersUsed"
              values={{
                p: (...chunks) => <p className="form-field-title">{chunks}</p>,
              }}
            />
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.areFertilizersUsed`}
                defaultChecked={isTrueString(defaultValues.areFertilizersUsed)}
                register={register({ required: true })}
                error={formErrors?.areFertilizersUsed}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.areFertilizersUsed`}
                defaultChecked={isTrueString(defaultValues.areFertilizersUsed)}
                register={register({ required: true })}
                error={formErrors?.areFertilizersUsed}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.fertilizersProducts"
              values={{
                p: (...chunks) => <p className="form-field-title">{chunks}</p>,
              }}
            />
            <div className="project-activity__array-fields">
              <ArrayFields
                name={`${tabKey}.fertilizersProducts`}
                control={control}
                register={register}
                rules={{ required: isTrueString(watchAreFertilizersUsed) }}
                errors={formErrors?.fertilizersProducts}
                disabled={disabled}
                projectDuration={projectData?.projectDuration}

              />
            </div>
          </div>
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.soilDisturbanceHappen' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.soilDisturbanceHappen`}
            value={defaultValues.soilDisturbanceHappen}
            register={register({ required: true })}
            error={formErrors?.soilDisturbanceHappen}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.isPruningPlanned' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            tooltip={getTooltip(translate.formatMessage({ id: 'carbon.form.isPruningPlannedTooltip' }), 'pruning-planned')}
            name={`${tabKey}.isPruningPlanned`}
            value={defaultValues.isPruningPlanned}
            register={register({ required: true })}
            error={formErrors?.isPruningPlanned}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.isThinningPlanned' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            tooltip={getTooltip(translate.formatMessage({ id: 'carbon.form.isThinningPlannedTooltip' }), 'thinning-planned')}
            name={`${tabKey}.isThinningPlanned`}
            value={defaultValues.isThinningPlanned}
            register={register({ required: true })}
            error={formErrors?.isThinningPlanned}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomInput
            type="text"
            label={translate.formatMessage({ id: 'carbon.form.treeSpeciesNumber' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.number' })}
            customClass={`input-field ${statusAlert?.treeSpeciesNumber ? 'warning' : ''}`}
            customClassLabel="form-field-title"
            name={`${tabKey}.treeSpeciesNumber`}
            value={defaultValues.treeSpeciesNumber}
            useNumberFormat
            numberFormatParams={{ allowNegative: true, thousandSeparator: false, decimalScale: 0 }}
            control={control}
            rules={{
              required: true,
              validate: {
                leadingZeros: (value) => +value[0] !== 0 || translate.formatMessage({ id: 'carbon.form.invalidValue' }),
              },
            }}
            change={(e) => {
              changeStatusAlert('treeSpeciesNumber', e.floatValue);
            }}
            error={formErrors?.treeSpeciesNumber}
            disabled={disabled}
          />
          {statusAlert?.treeSpeciesNumber && (
          <NotificationAlert text={(
            <FormattedMessage
              id="carbon.form.treeSpeciesNumber.warning"
              values={{
                u: (...chunks) => <u className="form-field-waring-under">{chunks}</u>,
              }}
            />
)}
          />
          )}

        </div>
        {Object.hasOwn(defaultValues, 'treeSpeciesPlanted') && (
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.treeSpeciesPlanted' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.treeSpeciesPlanted`}
            value={defaultValues.treeSpeciesPlanted}
            register={register({ required: true })}
            error={formErrors?.treeSpeciesPlanted}
            disabled={disabled}
          />
        </div>
        )}
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.areExoticSpeciesPlanted"
              values={{
                p: (...chunks) => <p className="form-field-title">{Object.hasOwn(defaultValues, 'treeSpeciesPlanted') ? [[upNumberString(chunks[0][0])]] : chunks}</p>,
              }}
            />
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.areExoticSpeciesPlanted`}
                defaultChecked={isTrueString(defaultValues.areExoticSpeciesPlanted)}
                register={register({ required: true })}
                error={formErrors?.areExoticSpeciesPlanted}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.areExoticSpeciesPlanted`}
                defaultChecked={isTrueString(defaultValues.areExoticSpeciesPlanted)}
                register={register({ required: true })}
                error={formErrors?.areExoticSpeciesPlanted}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={Object.hasOwn(defaultValues, 'treeSpeciesPlanted') ? upNumberString(translate.formatMessage({ id: 'carbon.form.exoticSpeciesList' })) : translate.formatMessage({ id: 'carbon.form.exoticSpeciesList' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.exoticSpeciesList`}
            value={defaultValues.exoticSpeciesList}
            register={register({ required: isTrueString(watchAreExoticSpeciesPlanted) })}
            error={formErrors?.exoticSpeciesList}
            disabled={disabled}
          />
        </div>
        {Object.hasOwn(defaultValues, 'nonNative') && (
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.nonNative' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.nonNative`}
            value={defaultValues.nonNative}
            register={register({ required: true })}
            error={formErrors?.nonNative}
            disabled={disabled}
          />
        </div>
        )}
        {Object.hasOwn(defaultValues, 'totalPlantedTree') && (
        <div className="project-activity__item">
          <CustomTextarea
            label={translate.formatMessage({ id: 'carbon.form.totalPlantedTree' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.totalPlantedTree`}
            value={defaultValues.totalPlantedTree}
            register={register({ required: true })}
            error={formErrors?.totalPlantedTree}
            disabled={disabled}
          />
        </div>
        )}
        <div className="project-activity__item">
          <CustomTextarea
            label={Object.hasOwn(defaultValues, 'totalPlantedTree') ? upNumberString(translate.formatMessage({ id: 'carbon.form.measuresPutInPlace' }), 0.03) : translate.formatMessage({ id: 'carbon.form.measuresPutInPlace' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.measuresPutInPlace`}
            value={defaultValues.measuresPutInPlace}
            register={register({ required: true })}
            error={formErrors?.measuresPutInPlace}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <CustomTextarea
            label={Object.hasOwn(defaultValues, 'totalPlantedTree') ? upNumberString(translate.formatMessage({ id: 'carbon.form.intendToDoWithForest' }), 0.03) : translate.formatMessage({ id: 'carbon.form.intendToDoWithForest' })}
            placeholder={translate.formatMessage({ id: 'carbon.placeholder.text' })}
            classTextarea="textarea-field"
            customClassLabel="form-field-title"
            name={`${tabKey}.intendToDoWithForest`}
            value={defaultValues.intendToDoWithForest}
            register={register({ required: true })}
            error={formErrors?.intendToDoWithForest}
            disabled={disabled}
          />
        </div>
        <div className="project-activity__item">
          <div>
            <FormattedMessage
              id="carbon.form.continueLightGroundMonitoring"
              values={{
                p: (...chunks) => <p className="form-field-title">{Object.hasOwn(defaultValues, 'totalPlantedTree') ? [[upNumberString(chunks[0][0], 0.03)]] : chunks}</p>,
              }}
            />
            <div>
              <CustomCheckbox
                type="radio"
                value="true"
                title={translate.formatMessage({ id: 'carbon.form.yes' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.continueLightGroundMonitoring`}
                defaultChecked={isTrueString(defaultValues.continueLightGroundMonitoring)}
                register={register({ required: true })}
                error={formErrors?.continueLightGroundMonitoring}
                disabled={disabled}
              />
              <CustomCheckbox
                type="radio"
                value="false"
                title={translate.formatMessage({ id: 'carbon.form.no' })}
                customClass="radio-button-wrapper"
                name={`${tabKey}.continueLightGroundMonitoring`}
                defaultChecked={isTrueString(defaultValues.continueLightGroundMonitoring)}
                register={register({ required: true })}
                error={formErrors?.continueLightGroundMonitoring}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectActivity;
